import PropTypes from 'prop-types';
import React, { createContext, useContext, useEffect, useState } from 'react';
import GeneralErrorPage from './GeneralErrorPage.js';
import { getErrorPagesByType } from './errorPagesHelpers.js';
import useBrowserVersion from './useBrowserVersion.js';
import { useNameConventions } from '../../../contexts/nameConventionsContext.js';

const initialProps = {
  isOffline: process.env.WORK_OFFLINE === 'false' && typeof navigator !== 'undefined' && !navigator.onLine,
  internalError: false,
};

export const ErrorPagesContext = createContext(initialProps);

const ErrorPagesContextProvider = ({ children }) => {
  const [errorFlags, setErrorFlags] = useState(initialProps);

  return (
    <ErrorPagesContext.Provider value={{errorFlags, setErrorFlags}}>
      {children}
    </ErrorPagesContext.Provider>
  );
};

ErrorPagesContextProvider.propTypes = {
  children: PropTypes.node
};

export default ErrorPagesContextProvider;

export const useErrorPages = () => {
  const { browserSupported } = useBrowserVersion();
  const { errorFlags, setErrorFlags } = useContext(ErrorPagesContext);
  const { nameConventions } = useNameConventions();
  useEffect(() => {
    const handleOffline = () => setIsOffline(true);
    const handleOnline = () => setIsOffline(false);

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  useEffect(() => { 
    setErrorFlags({
      ...errorFlags,
      unsupportedBrowser: !browserSupported,
    });
  }, [browserSupported]);

  const setIsOffline = (value) => {
    if (process.env.WORK_OFFLINE === 'false') {
      setErrorFlags({
        ...errorFlags,
        isOffline: value,
      });
    }
  };

  const setInternalError = (value) => {
    setErrorFlags({
      ...errorFlags,
      internalError: value,
    });
  };

  const getErrorPage = () => {
    if (errorFlags.isOffline) {
      return <GeneralErrorPage type={getErrorPagesByType(nameConventions).PAGE_OFFLINE} />;
    }

    if (errorFlags.internalError) {
      return <GeneralErrorPage type={getErrorPagesByType(nameConventions).PAGE_INTERNAL_ERROR} />;
    }

    if (errorFlags.unsupportedBrowser) {
      return <GeneralErrorPage type={getErrorPagesByType(nameConventions).PAGE_UNSUPPORTED_BROWSER} />;
    }

    return <div>SOMETHING WEIRD HAPPENS!</div>;
  };

  const hasPageError = Object.values(errorFlags).some(value => value === true);  

  return {
    hasPageError,
    setIsOffline,
    setInternalError,
    getErrorPage
  };
};
